import { ThreadStateEnum } from '@anecdotes/commenting';
import { Injectable } from '@angular/core';
import { DefaultProjectorFn, MemoizedSelector, Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { ThreadViewModel, CommentingUserEventTypes, CommentingEventDataProperty, CommentingEventData } from '../../models';
import { createResourceLogDataSelector, selectCommonLogData, selectDisplayedThreadViewModels, selectThreadViewModels } from '../../store/selectors';
import { AbstractCommentingUiConfigurationProvider } from '../abstract-commenting-ui-configuration-provider/abstract-commenting-ui-configuration-provider.service';

@Injectable()
export class CommentingUserEventsService {
  constructor(private store: Store, private configProvider: AbstractCommentingUiConfigurationProvider) {}

  trackResolveAllEventAsync(threadCountToResolve: number): Promise<void> {
    return this.sendEventAsync(CommentingUserEventTypes.ResolveAllThreads, {
      [CommentingEventDataProperty.numberOfOpenChats]: threadCountToResolve,
    });
  }

  async trackDeleteAllResolvedEvent(threadCountToResolve: number): Promise<void> {
    return this.sendEventAsync(CommentingUserEventTypes.DeleteAllResolved, {
      [CommentingEventDataProperty.numberOfChats]: threadCountToResolve,
    });
  }

  async trackResolveThreadEventAsync(threadId: string): Promise<void> {
    const logData = await this.getThreadLogDataAsync(threadId);

    return this.sendEventAsync(CommentingUserEventTypes.ResolveThread, {
      ...logData,
    });
  }

  async trackEditThreadEventAsync(threadId: string, hasMentioning: boolean): Promise<void> {
    const logData = await this.getThreadLogDataAsync(threadId);

    return this.sendEventAsync(CommentingUserEventTypes.EditThread, {
      ...logData,
      [CommentingEventDataProperty.mentioned]: hasMentioning ? 'yes' : 'no',
    });
  }

  async trackDeleteThreadEventAsync(threadId: string): Promise<void> {
    const threadViewModel = await this.getThreadViewModelAsync(threadId);
    const eventType =
      threadViewModel.threadState === ThreadStateEnum.Resolved
        ? CommentingUserEventTypes.DeleteResolvedThread
        : CommentingUserEventTypes.DeleteThread;

    return this.sendEventAsync(eventType, {
      ...threadViewModel.logData,
    });
  }

  async trackActivateThreadEventAsync(threadId: string): Promise<void> {
    const logData = await this.getThreadLogDataAsync(threadId);

    return this.sendEventAsync(CommentingUserEventTypes.ActivateThread, {
      ...logData,
    });
  }

  async trackThreadCreationEventAsync(resourceType: string, resourceId: string, hasMentioning: boolean): Promise<void> {
    const logData = await this.selectAsync(createResourceLogDataSelector(resourceType, resourceId));

    return this.sendEventAsync(CommentingUserEventTypes.CreateThread, {
      ...logData,
      [CommentingEventDataProperty.mentioned]: hasMentioning ? 'yes' : 'no',
    });
  }

  async trackReplyCreationEventAsync(
    resourceType: string,
    resourceId: string,
    hasMentioning: boolean,
    numberOfRepliesBefore: number,
  ): Promise<void> {
    const logData = await this.selectAsync(createResourceLogDataSelector(resourceType, resourceId));

    return this.sendEventAsync(CommentingUserEventTypes.CreateReply, {
      ...logData,
      [CommentingEventDataProperty.mentioned]: hasMentioning ? 'yes' : 'no',
      [CommentingEventDataProperty.numberOfRepliesBefore]: numberOfRepliesBefore,
    });
  }

  trackDisplaySingleThreadEvent(): Promise<void> {
    return this.sendEventAsync(CommentingUserEventTypes.DisplaySingleThread, {});
  }

  trackCloseSingleThreadDisplayEvent(): Promise<void> {
    return this.sendEventAsync(CommentingUserEventTypes.CloseSingleThreadDisplay, {});
  }

  async trackFilterToActiveThreadEventAsync(): Promise<void> {
    const displayedThreads = await this.selectAsync(selectDisplayedThreadViewModels);

    return this.sendEventAsync(CommentingUserEventTypes.FilterToActiveThreads, {
      [CommentingEventDataProperty.numberOfChats]: displayedThreads.length,
    });
  }

  async trackOpeningSidebarViaCTA(resourceType: string, resourceId: string): Promise<void> {
    const logData = await this.selectAsync(createResourceLogDataSelector(resourceType, resourceId));
    const displayedThreads = await this.selectAsync(selectDisplayedThreadViewModels);

    return this.sendEventAsync(CommentingUserEventTypes.OpenSidebarViaCTA, {
      ...logData,
      [CommentingEventDataProperty.numberOfChats]: displayedThreads.length,
    });
  }

  async trackFilterToMentionedThreadEventAsync(): Promise<void> {
    const displayedThreads = await this.selectAsync(selectDisplayedThreadViewModels);

    return this.sendEventAsync(CommentingUserEventTypes.FilterToMentionedThreads, {
      [CommentingEventDataProperty.numberOfChats]: displayedThreads.length,
    });
  }

  async trackFilterToVisibleForAuditorThreadEventAsync(): Promise<void> {
    const displayedThreads = await this.selectAsync(selectDisplayedThreadViewModels);

    return this.sendEventAsync(CommentingUserEventTypes.FilterToVisibleForAuditorThreads, {
      [CommentingEventDataProperty.numberOfChats]: displayedThreads.length,
    });
  }

  async trackFilterToResolvedThreadEventAsync(): Promise<void> {
    const displayedThreads = await this.selectAsync(selectDisplayedThreadViewModels);

    return this.sendEventAsync(CommentingUserEventTypes.FilterToResolvedThreads, {
      [CommentingEventDataProperty.numberOfChats]: displayedThreads.length,
    });
  }

  async trackFocusThreadEventAsync(resourceType: string, resourceId: string, numberOfComments: number): Promise<void> {
    const logData = await this.selectAsync(createResourceLogDataSelector(resourceType, resourceId));
    return this.sendEventAsync(CommentingUserEventTypes.FocusThread, {
      ...logData,
      [CommentingEventDataProperty.numberOfComments]: numberOfComments,
    });
  }

  private async getThreadViewModelAsync(threadId: string): Promise<ThreadViewModel> {
    const threadViewModels = await this.selectAsync(selectThreadViewModels);
    return threadViewModels.find((tvm) => tvm.threadId === threadId);
  }

  private async getThreadLogDataAsync(threadId: string): Promise<any> {
    const threadViewModel = await this.getThreadViewModelAsync(threadId);
    return await this.selectAsync(
      createResourceLogDataSelector(threadViewModel.resourceType, threadViewModel.resourceId)
    );
  }

  private selectAsync<T>(selector: MemoizedSelector<object, T, DefaultProjectorFn<T>>): Promise<T> {
    return firstValueFrom(this.store.select(selector));
  }

  private async sendEventAsync<T = CommentingEventData>(eventType: string, eventData: T): Promise<void> {
    const commonLogData = await this.selectAsync(selectCommonLogData);
    this.configProvider.sendUserEvent(eventType, {
      ...commonLogData,
      ...eventData,
    });
  }
}
